import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import clsx from 'clsx'
import { iconNames } from 'lib/settings/icons'
import Icon from '../Icon/Icon'
import Link from '../Link/Link'
import { PrimaryHeaderOption } from './SiteHeader'

type IconKey = (typeof iconNames)[number]

interface DropdownLinksProps {
  /**
   * Icon name of the dropdown button
   */
  icon: IconKey
  /**
   * Array of object to display for the dropdown
   * eg. [{ name: 'lorem isum', href: 'sample.com', onClick: () => console.log('clicked') }]
   */
  options: PrimaryHeaderOption[]
  /**
   * Text to display to the right of the dropdown button
   */
  accountText?: string
  /**
   * Accessible label announced by assistive tech
   */
  ariaLabel?: string
  /**
   * Secondary header style
   */
  secondary?: boolean
  /**
   * Function to call when the account text is clicked
   * Only used in secondary header
   */
  onIconClick?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void
}

export default function DropdownLinks({
  icon,
  options,
  accountText,
  ariaLabel,
  secondary,
  onIconClick,
}: DropdownLinksProps) {
  return (
    <Menu>
      <MenuButton
        className="flex items-center focus:outline-none focus-visible:outline-none"
        onClick={(e) => onIconClick?.(e) || undefined}
        aria-label={ariaLabel ?? accountText ?? undefined}
      >
        <Icon
          icon={icon}
          className={clsx('w-6 h-6 align-middle', secondary && 'lg:w-7 lg:h-7')}
        />
        {accountText && (
          <span className="ml-1 max-w-[78px] [@media(min-width:470px)]:max-w-44  overflow-hidden whitespace-nowrap truncate">
            {accountText}
          </span>
        )}
      </MenuButton>
      <MenuItems
        anchor="bottom start"
        className="focus:outline-none focus-visible:outline-none z-10 flex flex-col bg-white rounded-lg mt-[6px] shadow-[0_0_10px_0_rgba(0,0,0,0.1)]"
      >
        {options.map((option, key) => (
          <MenuItem
            key={key}
            as={Link}
            disabled={option.disabled}
            {...('target' in option ? { target: option.target } : undefined)}
            {...('href' in option
              ? { href: option.href }
              : { onClick: option.onClick })}
            inheritFont
            className="data-[focus]:bg-neutral-200 min-w-max !p-4 type-body-6 no-underline rounded-none border-x-0 border-t-0 border-b last:border-b-0 !border-neutral-400 text-theme-primary hover:bg-neutral-200"
          >
            {option.name}
          </MenuItem>
        ))}
      </MenuItems>
    </Menu>
  )
}
